import { useCallback, useEffect, useState } from 'react'
import {
  X, User, FileText, ListMusic, HeartHandshake,
} from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { trackMxEvent } from '@/utils/mixpanel'
import { SERVICE_LIST } from '@/data/service'
import useAuth from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { SOUND_REVIEW_SLUG } from '@/config/sound-review'

export default function HeaderMobileMenu() {
  const router = useRouter()
  const { isLoggedin, currentUser } = useAuth()
  const [ isOpen, setIsOpen ] = useState(false)

  const handleRouteChange = useCallback(() => { setIsOpen(false) }, [])
  const handleOpenMenu = useCallback(() => setIsOpen(true), [])
  const handleCloseMenu = useCallback(() => setIsOpen(false), [])

  const handleServiceCategoryClick = useCallback((service: typeof SERVICE_LIST[number]) => {
    trackMxEvent('Header Service Category Click', {
      service_category_name: service.service.replace('\n', ''),
      service_category_slug: service.slug,
    })
  }, [])

  const handleServiceClick = useCallback(() => trackMxEvent('Header Service Click'), [])

  const handleSoundReviewClick = useCallback(() => {
    trackMxEvent('Header Service Category Click', {
      service_category_name: 'サウンドレビュー',
      service_category_slug: SOUND_REVIEW_SLUG,
    })
  }, [])

  useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChange)
    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [ router.events, handleRouteChange ])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [ isOpen ])

  return (
    <>
      <div className="-ml-2 lg:hidden">
        <Button
          variant="ghost"
          size="icons"
          onClick={handleOpenMenu}
          className="relative"
          aria-label="Toggle mobile menu"
        >
          <span className="sr-only">モバイルメニュー</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <title>Menu Icon</title>
            <line x1="4" x2="20" y1="6" y2="6" />
            <line x1="4" x2="15" y1="12" y2="12" />
            <line x1="4" x2="10" y1="18" y2="18" />
          </svg>
        </Button>
      </div>
      {/* 元は framer-motion の 0.2 秒フェードインだったが、AnimatePresence で
          囲んでいないため exit は動いていなかった。実際に動いていた挙動を
          tw-animate-css で再現し、framer-motion への依存を外している
          （Layout 経由で全ページの共通チャンクに 43KB 載っていた）。 */}
      {isOpen && (
        <div
          className="animate-in fade-in-0 fixed inset-0 z-50 h-dvh overflow-y-auto overscroll-contain bg-background duration-200"
        >
          {/* dvh 非対応ブラウザでも下端が隠れないよう pb で余白を確保する */}
          <div className="flex w-full flex-col space-y-4 px-4 pb-[calc(6rem+env(safe-area-inset-bottom))]">
            <div className="flex-1 space-y-4">
              <div className="-ml-2 flex h-14 items-center">
                <Button
                  variant="ghost"
                  size="icons"
                  onClick={handleCloseMenu}
                  className="relative"
                  aria-label="Close menu"
                >
                  <span className="sr-only">閉じる</span>
                  <X className="h-6 w-6" />
                </Button>
              </div>
              <div className="space-y-8">
                {/* 募集中のプロジェクト */}
                {currentUser?.isProfessional && (
                  <div>
                    <Link
                      href="/competition/list"
                      className="flex items-center gap-4 py-2"
                    >
                      <FileText className="h-5 w-5" />
                      <span className="font-medium">募集中のプロジェクト</span>
                    </Link>
                  </div>
                )}

                {/* サービス */}
                <div>
                  <div className="flex items-center gap-4 py-2">
                    <ListMusic className="h-5 w-5" />
                    <span className="font-medium">サービス</span>
                  </div>
                  <ul className="mt-1">
                    <li>
                      <Link
                        className="flex items-center gap-2 py-2 pl-9 text-ghost"
                        href="/service/consulting/sound-review"
                        onClick={handleSoundReviewClick}
                      >
                        サウンドレビュー
                        <span className="rounded bg-[#FF8400] px-1 text-xs leading-5 font-medium text-white">
                          NEW
                        </span>
                      </Link>
                    </li>
                    {SERVICE_LIST.map((service) => (
                      <li key={service.slug}>
                        <Link
                          className="block py-2 pl-9 text-ghost"
                          href={`/service/${service.slug}`}
                          onClick={() => handleServiceCategoryClick(service)}
                        >
                          {service.service.replace('\n', '')}
                        </Link>
                      </li>
                    ))}
                    <li>
                      <Link
                        className="block py-2 pl-9 text-ghost"
                        href={`/service/${SERVICE_LIST[0].slug}`}
                        onClick={handleServiceClick}
                      >
                        サービス一覧
                      </Link>
                    </li>
                  </ul>
                </div>

                {/* クリエイターを探す */}
                <div>
                  <Link href="/search" className="flex items-center gap-4 py-2">
                    <User className="h-5 w-5" />
                    <span className="font-medium">
                      クリエイターを探す
                    </span>
                  </Link>
                  <ul className="mt-1">
                    <li>
                      <Link
                        className="block py-2 pl-9 text-ghost"
                        href="/cat/mix-engineer"
                      >
                        ミックスエンジニア
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="block py-2 pl-9 text-ghost"
                        href="/cat/arranger"
                      >
                        アレンジャー
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="block py-2 pl-9 text-ghost"
                        href="/cat/beat-maker"
                      >
                        ビートメーカー
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="block py-2 pl-9 text-ghost"
                        href="/cat/song-writer"
                      >
                        作曲家
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="block py-2 pl-9 text-ghost"
                        href="/cat/drummer"
                      >
                        ドラマー
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="block py-2 pl-9 text-ghost"
                        href="/cat/producer"
                      >
                        プロデューサー
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="block py-2 pl-9 text-ghost"
                        href="/search"
                      >
                        カテゴリーから探す
                      </Link>
                    </li>
                  </ul>
                </div>

                {/* ONLIVE Studio が目指すもの */}
                <div>
                  <Link
                    href="/for/you"
                    className="flex items-center gap-4 py-2"
                  >
                    <HeartHandshake className="h-5 w-5" />
                    <span className="font-medium">
                      ONLIVE Studio が目指すもの
                    </span>
                  </Link>
                  <ul className="mt-1">
                    <li>
                      <Link
                        className="block py-2 pl-9 text-ghost"
                        href="/for/artist"
                      >
                        お仕事を依頼する
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="block py-2 pl-9 text-ghost"
                        href="/for/pro"
                      >
                        依頼を受ける
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="block py-2 pl-9 text-ghost"
                        href="/how-to-use"
                      >
                        ONLIVE Studio の活用シーン
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            {!isLoggedin && (
              <>
                <Button variant="primary" className="w-full">
                  <Link href="/signup">アカウント登録</Link>
                </Button>
                <Button variant="ghost" className="w-full">
                  <Link href="/signin">ログイン</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}
